// src/components/materials/useMaterialFile.ts
//
// Fetching a material's RENDERED FILE (preview HTML, or an export).
//
// ============================================================================
// WHY THIS IS NOT AN <a href> OR AN <iframe src>
// ============================================================================
//
// The obvious implementation - point an anchor or an iframe at
// `/api/materials/:id/export/pdf` - is wrong in this app, and wrong in a way
// that only shows up for some users.
//
// CRM authenticates requests TWO ways:
//
//   • Bearer  - the browser holds the token in Redux and RTK Query injects an
//               `Authorization` header. A plain href or iframe src cannot
//               carry a header, so those requests arrive unauthenticated.
//   • RP/BFF  - the browser holds a `crm_sid` cookie and
//               `rpSessionAuthBridge` projects it onto the request. Here an
//               href WOULD work.
//
// So an href-based download works on a cookie session and 401s on a Bearer
// session, in the same build, depending on how that workspace signed in. That
// is the worst kind of bug: environment-dependent, invisible in development,
// and it reads to the user as "the export button is broken sometimes".
//
// Fetching explicitly is the one path that works in both modes - the same
// token RTK uses, plus `credentials: 'include'` for the cookie mode - and it
// also lets us tell a real failure from a rendered file, which an iframe
// silently cannot.
//
// ============================================================================
// WHY THE PREVIEW IS A BLOB IN A SANDBOXED IFRAME
// ============================================================================
//
// The preview is PitchLess-rendered HTML. It must never be injected into the
// CRM document: `dangerouslySetInnerHTML` would give a generated document the
// same origin, the same DOM and the same session as CRM itself.
//
// So it is rendered in an iframe whose `src` is a `blob:` URL and whose
// `sandbox` attribute is EMPTY - no `allow-scripts`, no `allow-same-origin`.
// That combination puts the document in an opaque origin with scripting
// disabled: it can paint, and it can do nothing else. The two flags must stay
// absent together; `allow-scripts allow-same-origin` would let the frame
// remove its own sandbox.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store/store';

export type MaterialFileFormat = 'docx' | 'pdf' | 'html';

interface FetchedFile {
  objectUrl: string;
  filename: string;
  contentType: string;
}

/** Read the same token RTK Query sends, from the same place. */
function useAccessToken(): string | null {
  return useSelector((state: RootState) => {
    const central = state.centralAuth?.accessToken;
    const legacy = state.auth?.token;
    return central || legacy || null;
  });
}

function pathFor(id: string, format: MaterialFileFormat, versionId?: string): string {
  const q = versionId ? `?versionId=${encodeURIComponent(versionId)}` : '';
  return `/api/materials/${encodeURIComponent(id)}/export/${format}${q}`;
}

/**
 * Pull the filename out of Content-Disposition, and never trust it.
 *
 * It reaches `<a download>`, so a separator or traversal in it would be a
 * suggestion about where to write. The server sanitises this too; doing it
 * again here costs nothing and means neither side is the only guard.
 */
function filenameFrom(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const m = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  if (!m) return fallback;
  const cleaned = m[1]
    .trim()
    .replace(/[\r\n"\\/]/g, '')
    .replace(/\.\./g, '');
  return cleaned.length > 0 ? cleaned.slice(0, 128) : fallback;
}

interface UseMaterialFileResult {
  /** Fetch and return an object URL. Caller decides what to do with it. */
  fetchFile: (
    id: string,
    format: MaterialFileFormat,
    versionId?: string
  ) => Promise<FetchedFile | null>;
  /** Fetch and hand the user a download. */
  download: (id: string, format: MaterialFileFormat, versionId?: string) => Promise<void>;
  pending: boolean;
  error: string | null;
  clearError: () => void;
}

export function useMaterialFile(): UseMaterialFileResult {
  const token = useAccessToken();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Every object URL this hook mints, so none of them leaks. A blob URL pins
  // its blob in memory until revoked, and a rendered deck is not small.
  const minted = useRef<string[]>([]);
  useEffect(
    () => () => {
      minted.current.forEach((u) => URL.revokeObjectURL(u));
      minted.current = [];
    },
    []
  );

  const fetchFile = useCallback(
    async (
      id: string,
      format: MaterialFileFormat,
      versionId?: string
    ): Promise<FetchedFile | null> => {
      setPending(true);
      setError(null);
      try {
        const res = await fetch(pathFor(id, format, versionId), {
          method: 'GET',
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!res.ok) {
          // The BFF's codes, not upstream text. 503 is a setup problem and
          // must not read as something the user can retry.
          let code: string | null = null;
          try {
            code = ((await res.json()) as { code?: string }).code ?? null;
          } catch {
            code = null;
          }
          setError(
            res.status === 503 || code === 'not_configured'
              ? 'PitchLess is not connected in this environment.'
              : res.status === 409
                ? 'This material has no content to export yet.'
                : res.status === 403
                  ? 'You do not have access to this material.'
                  : res.status === 404
                    ? 'This material no longer exists.'
                    : 'Could not produce the file. Please try again.'
          );
          return null;
        }

        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        minted.current.push(objectUrl);

        return {
          objectUrl,
          filename: filenameFrom(res.headers.get('content-disposition'), `material.${format}`),
          contentType: res.headers.get('content-type') || 'application/octet-stream',
        };
      } catch {
        setError('Could not reach the server. Please try again.');
        return null;
      } finally {
        setPending(false);
      }
    },
    [token]
  );

  const download = useCallback(
    async (id: string, format: MaterialFileFormat, versionId?: string): Promise<void> => {
      const file = await fetchFile(id, format, versionId);
      if (!file) return;
      const a = document.createElement('a');
      a.href = file.objectUrl;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    [fetchFile]
  );

  return {
    fetchFile,
    download,
    pending,
    error,
    clearError: useCallback(() => setError(null), []),
  };
}
